<template>
  <div class="cancel-flow">
    <!-- Terminal confirmation replaces the whole flow -->
    <FlowConfirmation
      v-if="terminal === 'cancelled'"
      title="Until next time!"
      body-prefix="Your PRO+ access will remain until "
      :bold="expiryShort || ''"
      body-suffix=". After that, you'll switch to the free plan."
      @continue="$emit('done')"
    />
    <FlowConfirmation
      v-else-if="terminal === 'paused'"
      title="See you soon!"
      :body-prefix="confirmationPrefix"
      :bold="resumesOn"
      body-suffix="."
      @continue="$emit('done')"
    />

    <template v-else>
      <button type="button" class="btn btn-link px-0 mb-2 text-decoration-none" @click="back">
        <span class="material-icons align-middle">chevron_left</span> Back
      </button>

      <!-- The store hand-off isn't part of a mandatory sequence, so it carries
           no progress bar; the optional retention steps after it count on their
           own. Stripe's 5-step bar is unchanged. -->
      <ProgressBar v-if="stepKind !== 'store'" :current="progressCurrent" :total="progressTotal" />

      <div v-if="reminderToast" class="alert alert-info py-2">Reminder scheduled — confirmation coming soon</div>

      <StoreCancelNotice v-if="stepKind === 'store'" :title="storeCopy.title" :body="storeCopy.body" />
      <WhatYoullLose v-else-if="stepKind === 'lose'" />
      <AccidentalRenewal v-else-if="stepKind === 'renewal'" :reminder-on="reminderOn || ''" />
      <ReasonPicker
        v-else-if="stepKind === 'reason'"
        v-model:selected="selectedReason"
        v-model:otherText="otherText"
        @not-working-selected="showSupportModal = true"
      />
      <PauseDurationChips v-else-if="stepKind === 'pause'" v-model="pauseMonths" />
      <FinalConfirmation v-else-if="stepKind === 'final'" :expiry-on="expiryShort" />

      <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>

      <!-- Persistent bottom button bar -->
      <div class="cancel-flow__actions mt-4">
        <template v-if="stepKind === 'store'">
          <button type="button" class="btn btn-primary w-100" @click="openStore">
            {{ storeCopy.cta }}
          </button>
          <button
            type="button"
            class="btn btn-link w-100 mt-2 text-decoration-underline"
            @click="advance"
          >
            Before you go, two quick things
          </button>
          <button
            type="button"
            class="btn btn-link w-100 mt-1 text-decoration-underline"
            @click="$emit('close')"
          >
            No thanks, I'm done
          </button>
        </template>
        <template v-else-if="stepKind === 'final'">
          <button type="button" class="btn btn-primary w-100 mb-2" @click="$emit('close')">
            Keep my subscription
          </button>
          <button type="button" class="btn btn-danger w-100" :disabled="submitting" @click="doCancel">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            Cancel PRO+ subscription
          </button>
        </template>
        <template v-else>
          <button type="button" class="btn btn-primary w-100" :disabled="submitting" @click="onPrimary">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ primaryLabel }}
          </button>
          <button
            v-if="linkLabel"
            type="button"
            class="btn btn-link w-100 mt-2 text-decoration-underline"
            @click="advance"
          >
            {{ linkLabel }}
          </button>
        </template>
      </div>
    </template>

    <NotWorkingSupportModal
      v-if="showSupportModal"
      @back="showSupportModal = false"
      @contact="contactSupportFromModal"
      @continue="continueFromSupport"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import graphClient from "@/utils/graphql/graph_client";
import { CancelWebSubscription, PauseWebSubscription } from "@/utils/graphql/subscriptions";
import { errorMessage } from "../../errorMessage";
import { contactSupport } from "../../support";
import {
  cancelFlowSteps,
  cancelReasonPayload,
  manageAction,
  pauseDurationEnum,
  storeCancelCopy,
  longOrdinalDate,
  shortDate,
  reminderDate,
  type CancelStep,
  pauseConfirmationPrefix,
  type SubscriptionSource,
  type CancelReasonSlug,
  type PillState,
} from "../../subscriptionLogic";
import FlowConfirmation from "../FlowConfirmation.vue";
import PauseDurationChips from "../PauseDurationChips.vue";
import AccidentalRenewal from "./AccidentalRenewal.vue";
import FinalConfirmation from "./FinalConfirmation.vue";
import NotWorkingSupportModal from "./NotWorkingSupportModal.vue";
import ProgressBar from "./ProgressBar.vue";
import ReasonPicker from "./ReasonPicker.vue";
import StoreCancelNotice from "./StoreCancelNotice.vue";
import WhatYoullLose from "./WhatYoullLose.vue";

const props = defineProps<{
  source: SubscriptionSource;
  expirationDate: string | null;
  managementUrl: string | null;
  // Drives whether the in-flow pause offer is worth showing; assume a plain
  // active sub when the caller doesn't say.
  pillState?: PillState;
}>();

const emit = defineEmits<{ (e: "close"): void; (e: "done"): void }>();

const currentStep = ref(0);
const selectedReason = ref<CancelReasonSlug | null>(null);
const otherText = ref("");
const pauseMonths = ref(1);
const submitting = ref(false);
const error = ref("");
const terminal = ref<null | "cancelled" | "paused">(null);
const resumesOn = ref("");
const confirmationPrefix = ref(pauseConfirmationPrefix(null));
const showSupportModal = ref(false);
const reminderToast = ref(false);

const steps = computed(() => cancelFlowSteps(props.source, props.pillState ?? "active"));
const totalSteps = computed(() => steps.value.length);
const stepKind = computed<CancelStep>(() => steps.value[currentStep.value]);
const isLastStep = computed(() => currentStep.value === totalSteps.value - 1);

// When the flow opens with the store hand-off, only the optional retention
// steps after it are counted by the progress bar.
const hasStoreNotice = computed(() => steps.value[0] === "store");
const progressTotal = computed(() => totalSteps.value - (hasStoreNotice.value ? 1 : 0));
const progressCurrent = computed(() => currentStep.value - (hasStoreNotice.value ? 1 : 0));

const storeCopy = computed(() => storeCancelCopy(manageAction(props.source, props.managementUrl)));

const expiryShort = computed(() => shortDate(props.expirationDate));
const reminderOn = computed(() => longOrdinalDate(reminderDate(props.expirationDate)));

const primaryLabel = computed(() => {
  switch (stepKind.value) {
    case "lose":
      return "Keep my subscription";
    case "renewal":
      return "Keep access, get reminder";
    case "reason":
      // Last step of the store flow: nothing to submit, just head back.
      return isLastStep.value ? "Done" : "Continue";
    case "pause":
      return "Pause my subscription";
    default:
      return "Continue";
  }
});

const linkLabel = computed(() => {
  switch (stepKind.value) {
    case "lose":
      return "Continue cancellation";
    case "renewal":
      // In the store flow nothing is being cancelled here — these steps are
      // optional, so the skip link says exactly that.
      return hasStoreNotice.value ? "Skip" : "Continue cancellation";
    case "pause":
      return "No thanks, continue cancellation";
    default:
      return "";
  }
});

function advance() {
  if (currentStep.value < totalSteps.value - 1) currentStep.value += 1;
}

function back() {
  if (currentStep.value > 0) currentStep.value -= 1;
  else emit("close");
}

function onPrimary() {
  switch (stepKind.value) {
    case "lose":
      emit("close");
      break;
    case "renewal":
      showReminderToast();
      break;
    case "reason":
      if (isLastStep.value) emit("close");
      else advance();
      break;
    case "pause":
      doPause();
      break;
  }
}

function showReminderToast() {
  reminderToast.value = true;
  window.setTimeout(() => {
    reminderToast.value = false;
  }, 4000);
}

function openStore() {
  const action = manageAction(props.source, props.managementUrl);
  if (action.kind === "store") {
    window.open(action.url, "_blank", "noopener");
  } else {
    // Sources with no self-serve page (promotional grants, Amazon, …).
    contactSupport();
  }
}

function contactSupportFromModal() {
  contactSupport();
  showSupportModal.value = false;
}

function continueFromSupport() {
  showSupportModal.value = false;
  advance();
}

async function doPause() {
  if (submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    const res: any = await graphClient.request(PauseWebSubscription, {
      input: { durationMonths: pauseDurationEnum(pauseMonths.value) },
    });
    // Both dates are backend-authoritative: the pause is anchored to the end of
    // the period already paid for, which the client can't know.
    const payload = res?.pauseWebSubscription;
    resumesOn.value = longOrdinalDate(payload?.resumesAt) || "";
    confirmationPrefix.value = pauseConfirmationPrefix(longOrdinalDate(payload?.accessEndsAt));
    terminal.value = "paused";
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}

async function doCancel() {
  // Only the Stripe flow has a final confirmation, but keep the guard local
  // too: the Stripe mutation must never fire for a store-billed sub.
  if (props.source !== "stripe" || submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    await graphClient.request(CancelWebSubscription, {
      input: { reason: cancelReasonPayload(selectedReason.value, otherText.value) },
    });
    terminal.value = "cancelled";
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}
</script>
